import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight } from "react-feather";
import styled from "styled-components";

import { hrefStyles, ML_BREAKPOINT, M_BREAKPOINT } from "../styles";
import { useActiveHeading } from "../hooks";

import { DOCS_HEADER_HEIGHT } from "./Header/Header";
import { MDXQuery } from "./mdx";

const Container = styled.aside`
  padding: var(--l) 0;
  position: relative;

  @media (max-width: ${ML_BREAKPOINT}px) {
    padding: 0;
  }
`;

const ContentContainer = styled.div`
  position: sticky;
  top: calc(${DOCS_HEADER_HEIGHT} + var(--l));

  /* Keep the sticky ToC within the viewport and let it scroll internally, so a
     long list can bring its active entry into view without moving the page. */
  @media (min-width: ${M_BREAKPOINT}px) {
    max-height: calc(100vh - ${DOCS_HEADER_HEIGHT} - var(--xl));
    overflow-y: auto;
    /* Avoid a scrollbar flashing over the text; only show when needed. */
    scrollbar-width: thin;
  }
`;

const Nav = styled.nav`
  display: flex;
  flex-direction: column;
`;

const Title = styled.h2`
  font-size: var(--sText);
  font-weight: var(--xBold);
  letter-spacing: 0.15rem;
  text-transform: uppercase;
  margin-bottom: var(--xl);
  color: var(--textColor);
  width: max-content;
  line-height: 1.2rem;
  display: flex;
  align-items: center;

  @media (max-width: ${M_BREAKPOINT}px) {
    margin-bottom: var(--m);
    cursor: pointer;
  }
`;

const NavItems = styled.ul<{ open: boolean }>`
  @media (max-width: ${M_BREAKPOINT}px) {
    display: ${({ open }) => (open ? "block" : "none")};
  }
`;

const Heading = styled.a<{ $active?: boolean }>`
  margin-bottom: 1rem;
  display: inline-block;
  color: ${({ $active }) =>
    $active ? "var(--accent)" : "var(--lightTextColor)"};
  font-size: var(--sText);
  line-height: 1.25rem;
  transition: color 0.15s ease;

  & > span {
    ${hrefStyles}
  }
`;

const Icon = styled.div`
  @media (min-width: ${M_BREAKPOINT}px) {
    display: none;
  }
`;

const SubItems = styled.ul`
  display: flex;
  flex-direction: column;
`;

interface ContentsProps {
  contents: MDXQuery["tableOfContents"];
}

/** All ToC urls, flattened in document order (e.g. "#§101-accounts"). */
const flattenUrls = (items: ContentsProps["contents"]["items"]): string[] =>
  (items ?? []).flatMap((item) => [
    item.url,
    ...flattenUrls(item.items ?? []),
  ]);

const Contents = ({ contents: { items } }: ContentsProps) => {
  const [open, setOpen] = useState(false);
  const activeHeadingId = useActiveHeading("#content");

  // The ToC lists only top-level sections, but the active heading may be a
  // deeper subheading not in the list. Map it to the last ToC entry that
  // appears at or before the active heading in the document, so the enclosing
  // section stays highlighted.
  const tocUrls = useMemo(() => flattenUrls(items), [items]);
  const activeUrl = useMemo(() => {
    if (!activeHeadingId) return null;
    const activeUrl = `#${activeHeadingId}`;
    if (tocUrls.includes(activeUrl)) return activeUrl;

    // Not a ToC entry itself — find the enclosing section by document order.
    if (typeof document === "undefined") return null;
    const headingIds = Array.from(
      document.querySelectorAll<HTMLElement>("#content [id]")
    ).map((el) => `#${el.id}`);
    const activeIndex = headingIds.indexOf(activeUrl);
    if (activeIndex === -1) return null;
    for (let i = activeIndex; i >= 0; i -= 1) {
      if (tocUrls.includes(headingIds[i])) return headingIds[i];
    }
    return null;
  }, [activeHeadingId, tocUrls]);

  // Keep the highlighted entry visible inside the ToC as the reader moves
  // through the document. We scroll ONLY the ToC's own container (referenced
  // directly — never the ancestor chain, which on mobile would climb to the
  // page-level scroll container and yank the reader). On mobile the ToC has no
  // internal overflow, so scrollHeight === clientHeight and this is a no-op.
  const scrollBoxRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLAnchorElement>(null);
  useEffect(() => {
    const reposition = () => {
      const box = scrollBoxRef.current;
      const link = activeRef.current;
      if (!box || !link) return;

      // Only when the ToC container itself scrolls (desktop). Never touch
      // anything outside this specific element.
      if (box.scrollHeight <= box.clientHeight) return;

      const linkRect = link.getBoundingClientRect();
      const boxRect = box.getBoundingClientRect();
      if (linkRect.top < boxRect.top) {
        box.scrollTop -= boxRect.top - linkRect.top;
      } else if (linkRect.bottom > boxRect.bottom) {
        box.scrollTop += linkRect.bottom - boxRect.bottom;
      }
    };

    reposition();
    // Re-run on resize so crossing the mobile/desktop breakpoint (which toggles
    // whether the ToC scrolls internally) repositions the active entry.
    window.addEventListener("resize", reposition, { passive: true });
    return () => window.removeEventListener("resize", reposition);
  }, [activeUrl]);

  return (
    <Container>
      <ContentContainer ref={scrollBoxRef}>
        {items?.length > 0 && (
          <Title
            role="button"
            aria-label="toggle table of contents"
            onClick={() => setOpen(!open)}
          >
            Table of Contents{" "}
            <Icon>
              {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            </Icon>
          </Title>
        )}
        <Nav>
          <NavItems open={open}>
            {items?.map((item, i) => (
              <li key={i}>
                <Heading
                  key={i}
                  href={item.url}
                  $active={activeUrl === item.url}
                  ref={activeUrl === item.url ? activeRef : undefined}
                >
                  <span>{item.title}</span>
                </Heading>
                {item.items && item.items?.length > 0 && (
                  <SubItems>
                    {item.items?.map((subitem, j) => (
                      <Heading
                        style={{ marginLeft: "var(--l)" }}
                        key={j}
                        href={subitem.url}
                        $active={activeUrl === subitem.url}
                        ref={activeUrl === subitem.url ? activeRef : undefined}
                      >
                        <span>{subitem.title}</span>
                      </Heading>
                    ))}
                  </SubItems>
                )}
              </li>
            ))}
          </NavItems>
        </Nav>
      </ContentContainer>
    </Container>
  );
};

export default Contents;
